import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Video,
  Crown,
  Trophy,
  Sparkles,
  Users,
  Gift,
  Gamepad2,
  History,
  Flame,
  Wand2,
  ChevronRight,
  Check,
} from "lucide-react";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { PAGE_NAME, UserModelData } from "../../../types";
import AppLayout from "../../../components/AppLayout";
import LoadingIndicator from "../../components/LoadingIndicator";
import CryptoAssetsSection from "../../components/CryptoAssetsSection";
import GrantAvailableCard from "../../components/GrantAvailableCard";
import GrantNotAvailableCard from "../../components/GrantNotAvailableCard";
import DepositCard from "../../components/DepositCard";
import HomeGuestView from "../../components/HomeGuestView";
import DailyRewardModal from "../../components/DailyRewardModal";
import FlashBoostCard from "../../components/FlashBoostCard";
import TurboChargeCard from "../../components/TurboChargeCard";
import DirectBoostCard from "../../components/DirectBoostCard";
import useThemeHook from "../../hooks/useThemeHook";
import useEnsureUser from "../../hooks/useEnsureUser";
import useAdHub from "../../hooks/useAdHub";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { actions } from "../../redux";
import { TURBO_ADS_REQUIRED } from "../../utils/rewardTables";

/**
 * HomePage — guests see the landing view, signed-in users the mining home.
 */
const HomePage = () => {
  const { status } = useEnsureUser();

  if (status === "checking") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <LoadingIndicator />
      </div>
    );
  }

  if (status !== "authenticated") {
    return <HomeGuestView />;
  }

  return (
    <AppLayout>
      <HomePageContent />
    </AppLayout>
  );
};

/** Exchange listing announcement — stays on Home so every user sees it. */
const ListingAnnouncementCard = () => {
  const navigate = useNavigate();
  // Opt-in ad: routed through the hub so it pays the ladder like any other ad.
  const { watchAd, isWatching } = useAdHub();

  return (
    <div className="relative overflow-hidden rounded-3xl p-5 border-2 border-indigo-500/30 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-2xl shadow-indigo-500/20">
      <div className="absolute -right-10 -top-10 w-28 h-28 bg-purple-500/25 rounded-full blur-2xl animate-pulse" />
      <div className="absolute -left-10 -bottom-10 w-28 h-28 bg-blue-500/25 rounded-full blur-2xl animate-pulse" />

      <div className="relative z-10 flex items-center justify-between mb-3">
        <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/40 text-[9px] font-black uppercase tracking-widest text-indigo-300">
          <Sparkles size={9} className="text-indigo-400" /> Listing Confirmed
        </span>
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Preparation Phase</span>
      </div>

      <div className="relative z-10 mb-2">
        <h3 className="text-base font-black tracking-tight leading-snug bg-gradient-to-r from-white via-indigo-100 to-indigo-300 bg-clip-text text-transparent">
          ARTHEREUM (ATH) LISTING SOON!
        </h3>
        <div className="flex flex-wrap gap-2 items-center mt-1">
          <span className="px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded text-[9px] font-black text-white uppercase tracking-wider shadow-sm">Gate.io</span>
          <span className="text-[10px] font-semibold text-indigo-400">and</span>
          <span className="px-2 py-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded text-[9px] font-black text-white uppercase tracking-wider shadow-sm">Bitunix</span>
        </div>
      </div>

      <p className="relative z-10 text-[11px] text-slate-300 leading-relaxed mb-3">
        We are listing on top crypto exchanges. Your loyalty made this possible — keep mining and growing your stash before trading starts.
      </p>

      <div className="relative z-10 flex gap-2">
        <button
          onClick={watchAd}
          disabled={isWatching}
          className="flex-1 flex items-center justify-center gap-1 py-2 px-2.5 bg-gradient-to-r from-amber-500 to-orange-500 active:scale-95 disabled:opacity-70 text-[10px] font-black uppercase text-white rounded-xl transition-all shadow-md shadow-orange-500/25"
        >
          <Video size={11} fill="currentColor" /> {isWatching ? "Loading…" : "Watch Ad"}
        </button>
        <button
          onClick={() => navigate(PAGE_NAME.WATCH_AND_EARN)}
          className="flex-1 flex items-center justify-center py-2 px-2.5 bg-white/10 active:scale-95 text-[10px] font-black uppercase text-white rounded-xl transition-all"
        >
          Watch &amp; Earn 📺
        </button>
      </div>
    </div>
  );
};

/** Daily reward card — the real, credited 7-day ladder. */
const DailyRewardCard = ({ onOpen }: { onOpen: () => void }) => {
  const theme = useThemeHook();
  const dark = theme === "dark";
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const claimedToday = !!user?.lastLoginRewardDate?.seconds &&
    new Date(user.lastLoginRewardDate.seconds * 1000).toDateString() === new Date().toDateString();
  const streak = user?.currentStreak || 1;
  const dayIndex = (streak - 1) % 7;
  const reward = arthereumProvider.DAILY_REWARDS[dayIndex];

  return (
    <button
      onClick={onOpen}
      className={`w-full text-left p-4 rounded-2xl border transition-all active:scale-[0.98] ${
        dark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-100 shadow-sm"
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${claimedToday ? (dark ? "bg-emerald-900/30" : "bg-emerald-50") : (dark ? "bg-indigo-900/30" : "bg-indigo-50")}`}>
          {claimedToday ? <Check size={22} className="text-emerald-500" /> : <Gift size={22} className="text-indigo-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold ${dark ? "text-white" : "text-gray-900"}`}>
            {claimedToday ? "Daily reward claimed" : `Daily reward · Day ${dayIndex + 1}`}
          </h3>
          <p className={`text-xs ${dark ? "text-slate-400" : "text-gray-500"}`}>
            {claimedToday ? "Come back tomorrow to keep your streak." : `+${reward?.ath ?? 0} ATH is waiting for you`}
          </p>
        </div>
        <ChevronRight size={18} className={dark ? "text-slate-500" : "text-gray-400"} />
      </div>
    </button>
  );
};

const HomePageContent = () => {
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const getTranslationText = useTranslationHook();
  const theme = useThemeHook();
  const dark = theme === "dark";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const adHub = useAdHub();

  const options = [
    { id: 1, title: getTranslationText("invite"), icon: <Users size={22} className="text-blue-500" />, onPress: () => navigate(PAGE_NAME.INVITE_USERS) },
    { id: 2, title: getTranslationText("watch"), icon: <Video size={22} className="text-amber-500" />, onPress: () => navigate(PAGE_NAME.WATCH_AND_EARN) },
    { id: 3, title: "Games", icon: <Gamepad2 size={22} className="text-emerald-500" />, onPress: () => navigate(PAGE_NAME.EARN) },
    { id: 4, title: "Lucky Spin", icon: <Wand2 size={22} className="text-purple-500" />, onPress: () => navigate(PAGE_NAME.LUCKY_SPIN) },
    { id: 5, title: "Go Pro", icon: <Crown size={22} className="text-amber-500" />, onPress: () => navigate(PAGE_NAME.PRO) },
    { id: 6, title: "Ranking", icon: <Trophy size={22} className="text-indigo-500" />, onPress: () => navigate(PAGE_NAME.LEADERBOARD) },
    { id: 7, title: "Shop", icon: <Sparkles size={22} className="text-pink-500" />, onPress: () => navigate(PAGE_NAME.SHOP) },
    { id: 8, title: getTranslationText("history"), icon: <History size={22} className="text-gray-500" />, onPress: () => navigate(PAGE_NAME.HYSTORY_PAGE) },
  ];

  // Referrals (cached in Redux)
  const referrals = useSelector((state: any) => state?.userReferrals);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(referrals?.length === 0);
  useEffect(() => {
    if (referrals?.length !== 0 || !userData?.user?.id) {
      setIsLoadingReferrals(false);
      return;
    }
    arthereumProvider
      .getUserReferrals(userData.user.id, null)
      .then((rrs) => dispatch(actions.setUserReferrals(rrs.referrals)))
      .catch(() => {})
      .finally(() => setIsLoadingReferrals(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user?.id]);

  // Grant availability (2h cycle)
  const [grantAvailable, setGrantAvailable] = useState(false);
  const [nextMiningDate, setNextMiningDate] = useState<Date | undefined>(undefined);
  useEffect(() => {
    const lastSeconds = userData.user?.lastMiningDate?.seconds;
    if (!lastSeconds) {
      setGrantAvailable(true);
      return;
    }
    const lastMining = new Date(lastSeconds * 1000);
    const diffHours = Math.abs(Date.now() - lastMining.getTime()) / 36e5;
    if (diffHours < 2) {
      setGrantAvailable(false);
      setNextMiningDate(new Date(lastMining.getTime() + 2 * 60 * 60 * 1000));
    } else {
      setGrantAvailable(true);
    }
  }, [userData]);

  // Timer ads now run app-wide via <AutoAdScheduler /> in AppLayout.

  const [showDailyReward, setShowDailyReward] = useState(false);
  const user = userData?.user;
  const streak = user?.currentStreak || 0;
  const luckyActive = !!user?.luckyTagExpiry?.seconds && user.luckyTagExpiry.seconds * 1000 > Date.now();
  const turboActive = !!user?.turboChargeExpiry?.seconds && user.turboChargeExpiry.seconds * 1000 > Date.now();
  const flashActive = !!user?.flashBoostExpiry?.seconds && user.flashBoostExpiry.seconds * 1000 > Date.now();

  return (
    <div className={`min-h-screen ${dark ? "bg-slate-900" : "bg-white"}`}>
      <div className="max-w-md mx-auto">
        <div className="px-3 py-3 pb-24 space-y-3">
          {/* Balance */}
          <div className={`rounded-2xl p-4 border ${dark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200 shadow-sm"}`}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {streak > 0 && (
                <span className="flex items-center gap-1 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-100 text-[10px] font-black text-orange-600 uppercase">
                  <Flame size={12} /> {streak} day streak
                </span>
              )}
              {luckyActive && <span className="bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100 text-[10px] font-black text-purple-600">LUCKY 2x ✨</span>}
              {turboActive && <span className="bg-yellow-50 px-2.5 py-1 rounded-full border border-yellow-100 text-[10px] font-black text-yellow-600">TURBO 5x ⚡</span>}
              {flashActive && <span className="bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100 text-[10px] font-black text-amber-600">FLASH 10x 🔥</span>}
            </div>
            <div className="text-center py-1">
              <div className={`text-4xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>
                {(user?.arthereumBalance ?? 0).toFixed(2)}
              </div>
              <div className={`text-sm mt-1 ${dark ? "text-slate-400" : "text-gray-400"}`}>Arthereum · {(user?.picoinBalance ?? 0).toFixed(2)} π</div>
            </div>
          </div>

          {/* Grant — the heartbeat */}
          {grantAvailable ? (
            <GrantAvailableCard nextGrantValue={userData?.miningInfos?.nextGrantValue} />
          ) : (
            <GrantNotAvailableCard targetDate={nextMiningDate} />
          )}

          {/* Listing announcement — always on Home */}
          <ListingAnnouncementCard />

          <DailyRewardCard onOpen={() => setShowDailyReward(true)} />

          <DepositCard onClick={() => navigate(PAGE_NAME.DEPOSIT)} />

          {/* Quick actions */}
          <div className="grid grid-cols-4 gap-2 px-1">
            {options.map((op) => (
              <button
                key={op.id}
                onClick={op.onPress}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-colors ${dark ? "active:bg-gray-700" : "active:bg-gray-100"}`}
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${dark ? "bg-gray-800" : "bg-gray-50 shadow-sm border border-gray-100"}`}>
                  {op.icon}
                </div>
                <span className={`text-[10px] font-medium text-center leading-none ${dark ? "text-gray-400" : "text-gray-600"}`}>{op.title}</span>
              </button>
            ))}
          </div>

          <FlashBoostCard />

          {/* Ad boosters */}
          <div className="space-y-3">
            {/* Ad Hub — unlimited ads, ladder ATH, Turbo progress */}
            <div className={`w-full p-4 rounded-2xl border ${dark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${dark ? "bg-amber-900/30" : "bg-amber-50"}`}>
                  <Video size={24} className="text-amber-500" fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold truncate ${dark ? "text-white" : "text-gray-900"}`}>Watch &amp; Earn 📺</h3>
                  <p className={`text-xs truncate ${dark ? "text-slate-400" : "text-gray-500"}`}>
                    Ads today {adHub.adsToday} · next ad{" "}
                    {adHub.athCapReached ? "+1 Turbo" : `+${adHub.nextAdAth} ATH`} · Turbo{" "}
                    {adHub.turboCount}/{TURBO_ADS_REQUIRED}
                  </p>
                </div>
                <button
                  onClick={() => navigate(PAGE_NAME.WATCH_AND_EARN)}
                  aria-label="Open the Ad Hub"
                  className={`p-1.5 rounded-lg transition-colors ${dark ? "active:bg-slate-700" : "active:bg-gray-100"}`}
                >
                  <ChevronRight size={18} className={dark ? "text-slate-500" : "text-gray-400"} />
                </button>
              </div>
              <button
                onClick={adHub.watchAd}
                disabled={adHub.isWatching}
                className="w-full mt-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-black uppercase tracking-wider active:scale-95 disabled:opacity-70 transition-all"
              >
                {adHub.isWatching ? "Loading ad…" : "Watch ad"}
              </button>
            </div>
            <TurboChargeCard count={user?.turboChargeCount || 0} expiry={user?.turboChargeExpiry} onClick={adHub.watchAd} />
            <DirectBoostCard />
          </div>

          {/* Referrals */}
          <div className="pt-2">
            {isLoadingReferrals ? (
              <div className="flex items-center justify-center py-8"><LoadingIndicator /></div>
            ) : (
              <CryptoAssetsSection onSeeMore={() => navigate(PAGE_NAME.INVITE_USERS)} referrals={referrals} />
            )}
          </div>
        </div>
      </div>

      {showDailyReward && <DailyRewardModal onClose={() => setShowDailyReward(false)} />}
    </div>
  );
};

export default HomePage;
